import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Navbar from './Navbar';
import LeftSidebar from './LeftSidebar';
import MobileBottomBar from './MobileBottomBar';
import { BottomBarProvider } from '../providers/BottomBarProvider';
import { MetricsProvider } from '../providers/MetricsProvider';
import { useUser } from '../providers/UserProvider';
import { cn } from '@/lib/utils';
import { X } from "lucide-react";

// Error Banner Component
const ErrorBanner = ({ message, onDismiss, theme = 'light' }) => {
  if (!message) return null;
  
  return (
    <div className="mb-4">
      <div className={cn(
        "flex items-center justify-between p-3 rounded-[6px] shadow-lg backdrop-blur-sm",
        theme === 'light'
          ? "bg-red-100/80 border border-red-500/50"
          : "bg-red-900/30 border border-red-500/30"
      )}>
        <span className={theme === 'light' ? "text-red-600" : "text-red-400"}>{message}</span>
        <button 
          onClick={onDismiss}
          className={cn(
            "hover:text-red-600 ml-2",
            theme === 'light' ? "text-red-500" : "text-red-400"
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const Navigation = ({ children }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const { user } = useUser();
  const [theme, setTheme] = useState('dark');

  const handleError = (error) => {
    if (error?.message) {
      setErrorMessage(error.message);
    } else if (typeof error === 'string') {
      setErrorMessage(error);
    }
  };

  const clearError = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    if (user?.theme) {
      setTheme(user.theme);
    }
  }, [user]);

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { 
        onError: handleError,
        theme: theme
      });
    }
    return child;
  });

  return (
    <MetricsProvider>
      <BottomBarProvider>
        <div className={cn(
          "min-h-screen pb-16 md:pb-0 relative isolate",
          theme === 'light' 
            ? "bg-white"
            : "bg-[#0B0F15]"
        )}>
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0B0F15] to-[#0B0F15]" />
          
          <Navbar onError={handleError} theme={theme} />
          <div className="hidden md:block fixed left-0 top-0 bottom-0 z-50">
            <LeftSidebar onError={handleError} theme={theme} />
          </div>
          <main className={cn(
            "pl-0 md:pl-20 relative z-0",
            theme === 'dark' && "text-gray-100"
          )}>
            <div>
              <ErrorBanner message={errorMessage} onDismiss={clearError} theme={theme} />
              <div className="m-0">
                {childrenWithProps}
              </div>
            </div>
          </main>
          <MobileBottomBar onError={handleError} theme={theme} />
        </div>
      </BottomBarProvider>
    </MetricsProvider>
  );
};

Navigation.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Navigation; 